import { createSlice } from "@reduxjs/toolkit";
import { IProductsState } from "./products.interface";
import {
  addProduct,
  deleteProduct,
  getProduct,
  getProducts,
  updateProduct,
} from "./products.action";

const initialState: IProductsState = {
  isProductsLoading: false,
  productsError: "",
  products: [],
  addProductModal: false,

  filters: {
    search: "",
    sort: "inserted",
    order: "desc",
    page: 0,
    limit: 10,
  },
  selectedProducts: [],
  selectedProduct: null,
  pagination: {
    length: 0,
    size: 0,
    page: 0,
  },

  // Get Product
  isGetProductLoading: false,
  getProductError: "",
  product: null,

  // Add Product
  isAddProductLoading: false,
  addProductSuccess: false,
  addProductError: "",

  // Edit Product
  isEditProductLoading: false,
  editProductSuccess: false,
  editProductError: "",

  // Delete Product
  isDeleteProductLoading: false,
  deleteProductSuccess: false,
  deleteProductError: "",
};

const productsSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setAddProductModal: (state, action) => {
      state.addProductModal = action.payload;
    },
    resetAddProduct: (state) => {
      state.isAddProductLoading = false;
      state.addProductSuccess = false;
      state.addProductError = "";
    },
    resetDeleteProduct: (state) => {
      state.isDeleteProductLoading = false;
      state.deleteProductSuccess = false;
      state.deleteProductError = "";
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setSelectedProducts: (state, action) => {
      state.selectedProducts = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    resetGetProduct: (state) => {
      state.isGetProductLoading = false;
      state.getProductError = "";
      state.product = null;
      state.selectedProduct = null;
    },
    resetEditProduct: (state) => {
      state.isEditProductLoading = false;
      state.editProductSuccess = false;
      state.editProductError = "";
      state.selectedProduct = null;
    },
  },
  extraReducers: (builder) => {
    // Get Products
    builder.addCase(getProducts.pending, (state) => {
      state.isProductsLoading = true;
    });

    builder.addCase(getProducts.fulfilled, (state, action) => {
      state.isProductsLoading = false;
      state.products = action.payload.products || [];
      state.pagination = action.payload.pagination;
    });

    builder.addCase(getProducts.rejected, (state, action) => {
      state.isProductsLoading = false;
      state.productsError = action.payload || "Failed to get products";
    });

    // Get Product
    builder.addCase(getProduct.pending, (state) => {
      state.isGetProductLoading = true;
    });

    builder.addCase(getProduct.fulfilled, (state, action) => {
      state.isGetProductLoading = false;
      state.selectedProduct = action.payload;
    });

    builder.addCase(getProduct.rejected, (state, action) => {
      state.isGetProductLoading = false;
      state.getProductError = action.payload || "Failed to get product";
    });

    // Add Product
    builder.addCase(addProduct.pending, (state) => {
      state.isAddProductLoading = true;
    });

    builder.addCase(addProduct.fulfilled, (state) => {
      state.isAddProductLoading = false;
      state.addProductSuccess = true;
    });

    builder.addCase(addProduct.rejected, (state, action) => {
      state.isAddProductLoading = false;
      state.addProductError = action.payload || "Failed to add product";
    });

    // Edit Product
    builder.addCase(updateProduct.pending, (state) => {
      state.isEditProductLoading = true;
    });

    builder.addCase(updateProduct.fulfilled, (state) => {
      state.isEditProductLoading = false;
      state.editProductSuccess = true;
    });

    builder.addCase(updateProduct.rejected, (state, action) => {
      state.isEditProductLoading = false;
      state.editProductError = action.payload || "Failed to update product";
    });

    // Delete Product
    builder.addCase(deleteProduct.pending, (state) => {
      state.isDeleteProductLoading = true;
    });

    builder.addCase(deleteProduct.fulfilled, (state) => {
      state.isDeleteProductLoading = false;
      state.deleteProductSuccess = true;
    });

    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.isDeleteProductLoading = false;
      state.deleteProductError = action.payload || "Failed to delete product";
    });
  },
});

export default productsSlice.reducer;

export const {
  setAddProductModal,
  resetAddProduct,
  resetDeleteProduct,
  setFilters,
  setSelectedProducts,
  setSelectedProduct,
  resetGetProduct,
  resetEditProduct,
} = productsSlice.actions;
